"use client";

import { useSearchParams } from "next/navigation";
import { toast } from "sonner";
import * as React from "react";

/**
 * Acknowledges a member arriving on the dashboard:
 *  - ?joined=1         → a brand-new FREE account, straight from signup. Nothing was paid.
 *  - ?property=pending → the property is added and awaiting our verification.
 *  - ?welcome=1        → membership payment authorized (Stripe's success_url).
 *  - ?checkout=cancel  → they backed out; nothing was charged.
 * The query param is cleared afterwards so a refresh doesn't re-toast. Renders nothing.
 * Must be wrapped in <Suspense> by the caller (useSearchParams requirement).
 *
 * 🔴 `joined` EXISTS BECAUSE `welcome` HAD TWO MEANINGS. The signup wizard redirected to
 * `?welcome=1` for a free account that had paid nothing, and this file read that as Stripe's
 * success return — so every new member was told "Payment authorized — welcome! Your membership is
 * being finalized by our team." Two senders, opposite meanings, one param. Keep them apart: only
 * the CRM's Stripe success_url may send `welcome`.
 */
export function CheckoutReturnToast() {
  const sp = useSearchParams();
  React.useEffect(() => {
    let message: string | null = null;
    let info = false;
    if (sp.get("joined") === "1") {
      message = "Welcome to CJ Owl — your free account is ready.";
    } else if (sp.get("property") === "pending") {
      message = "Property added — we're verifying it now. It'll activate once our team confirms it.";
    } else if (sp.get("welcome") === "1") {
      message = "Payment authorized — welcome! Your membership is being finalized by our team.";
    } else if (sp.get("checkout") === "cancel") {
      message = "Checkout canceled — nothing was charged.";
      info = true;
    }
    if (!message) return;
    if (info) toast(message);
    else toast.success(message);
    window.history.replaceState(null, "", window.location.pathname);
  }, [sp]);
  return null;
}
